import type { RepositoryIntelligenceAnalysisSummary } from '@ashniva/types';
import { Alert, Button, Card, SkeletonText, Spinner } from '@ashniva/ui';

import { formatDateTime } from '../../../shared/lib/format';
import { useAnalysisQuery } from '../api-changes';
import { shortCommit } from '../freshness';
import { ANALYSIS_STATUS_LABELS, analysisFailure } from '../ri-text';
import { useWorkspace } from '../workspace-context';
import { AnalysisReport } from './AnalysisReport';
import { ProblemNotice } from './ProblemNotice';
import { SuggestedTasks } from './SuggestedTasks';

/** What was analysed, in a few words. */
function describe(analysis: RepositoryIntelligenceAnalysisSummary): string {
  if (analysis.source === 'pull_request') {
    return `Pull request #${analysis.prNumber ?? '?'}`;
  }
  if (analysis.source === 'task_commits') {
    return `${analysis.commits?.length ?? 0} commits of a task`;
  }
  const base = analysis.baseRef ?? shortCommit(analysis.base) ?? '?';
  const head = analysis.headRef ?? shortCommit(analysis.head) ?? '?';
  return `${base} → ${head}`;
}

/** One analysis, followed until it is done, then its report and its suggested tasks (P14-07). */
export function AnalysisView({ analysisId }: { analysisId: string }) {
  const { linkId } = useWorkspace();
  const { query, stillWorking, checkAgain } = useAnalysisQuery(linkId, analysisId);

  if (query.isLoading) {
    return (
      <Card title="Analysis">
        <div aria-busy="true">
          <span className="sr-only">Loading the analysis</span>
          <SkeletonText lines={6} />
        </div>
      </Card>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Card title="Analysis">
        <ProblemNotice error={query.error} onRetry={() => void query.refetch()} />
      </Card>
    );
  }
  const result = query.data;
  const { analysis } = result;
  const final = analysis.pollAfterMs === null;
  return (
    <Card title="Analysis" subtitle={describe(analysis)}>
      <div className="ri-stack">
        <p role="status" aria-live="polite" className="ri-state__line">
          {final ? null : <Spinner size="sm" decorative />}
          {ANALYSIS_STATUS_LABELS[analysis.status]}
          <span className="ri-muted">
            {' '}
            · rules {analysis.rulesVersion} · asked at {formatDateTime(analysis.createdAt)}
          </span>
        </p>
        {stillWorking ? (
          <Alert
            tone="info"
            title="Still working"
            action={<Button onClick={checkAgain}>Check again</Button>}
          >
            This is taking longer than usual. Desk stopped checking so as not to keep asking; look
            again in a moment.
          </Alert>
        ) : null}
        {analysis.status === 'failed' ? (
          <Alert tone="danger" title="The analysis failed">
            {analysisFailure(analysis.errorCode) ?? 'No reason was given.'}
          </Alert>
        ) : null}
        {analysis.status === 'succeeded' ? (
          <>
            <AnalysisReport result={result} />
            <SuggestedTasks analysisId={analysis.id} suggestions={result.suggestedTasks} />
          </>
        ) : null}
      </div>
    </Card>
  );
}
